// ui/src/pages/AuctionAdmin.jsx
// Two-tab admin: SETUP (build the auction) + LIVE (run it).
// Backend routes used:
//   POST /auction/setup            { season_id, players_per_set, budget_per_team, admin_password, team_budgets }
//   POST /auction/random-set       { session_id }
//   POST /auction/next-player      { session_id }            (no player_id = random from active set)
//   POST /auction/next-player      { session_id, player_id } (manual override)
//   POST /auction/bid              { session_id, amount }
//   POST /auction/sold             { session_id, player_id, team_id, amount }
//   POST /auction/unsold           { session_id, player_id }
//   POST /auction/start-unsold-round       { session_id }
//   POST /auction/start-replacement-pool   { session_id }
//   apiService.endAuction / revertSold

import { useState, useCallback, useEffect, useRef } from 'react';
import useAuctionPoller from '../hooks/useAuctionPoller';
import { apiService } from '../services/api';
import api from '../services/api';
import useStore from '../store/store';
import Toast from '../components/Toast';
import './AuctionAdmin.css';

// ── Password gate ──────────────────────────────────────────────
function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!pw.trim()) { setErr('Enter the admin password'); return; }
    onUnlock(pw.trim());
  };
  return (
    <div className="admin-gate">
      <div className="admin-gate-card">
        <div className="gate-logo">VPL</div>
        <h2>Auction Admin</h2>
        <p className="gate-subtitle">Season 5 — Admin Access</p>
        <form onSubmit={handleSubmit} className="gate-form">
          <input type="password" placeholder="Admin password" value={pw}
            onChange={(e) => { setPw(e.target.value); setErr(''); }}
            className={`gate-input ${err ? 'gate-input-err' : ''}`}
            autoFocus autoComplete="off" />
          {err && <p className="gate-err">{err}</p>}
          <button type="submit" className="gate-btn">Enter</button>
        </form>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SETUP TAB — build the auction
// ════════════════════════════════════════════════════════════════
function SetupTab({ seasonId, adminPassword, hasSession, onBuilt, showToast }) {
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [playersPerSet, setPlayersPerSet] = useState(15);
  const [defaultBudget, setDefaultBudget] = useState(150);
  // overrides: { [teamId]: { budget, owner_slots } }
  const [overrides, setOverrides] = useState({});
  const [building, setBuilding] = useState(false);
  const [buildResult, setBuildResult] = useState(null);

  // Refresh-prices state
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null); // { done, total, henrik_ok, fallback }

  // Loop the batched refresh-prices route until done.
  const handleRefreshPrices = async () => {
    setRefreshing(true);
    setRefreshProgress(null);
    let offset = 0;
    let total = 0;
    let henrikOk = 0;
    let fallback = 0;
    try {
      // safety cap: max 150 batches (750 players at 5/batch)
      for (let i = 0; i < 150; i++) {
        const res = await api.post('/auction/refresh-prices', {
          season_id: seasonId,
          offset,
          limit: 5,
          admin_password: adminPassword,
        }, { timeout: 60000 });
        const d = res.data;
        total = d.total;
        henrikOk += d.henrik_ok;
        fallback += d.fallback;
        offset = d.next_offset;
        setRefreshProgress({ done: Math.min(offset, total), total, henrik_ok: henrikOk, fallback });
        if (d.done) break;
      }
      showToast(`Prices refreshed — ${total} players (${henrikOk} via Henrik, ${fallback} fallback)`);
    } catch (err) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Refresh failed';
      showToast(msg, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!seasonId) return;
    setLoadingTeams(true);
    api.get(`/season_teams/${seasonId}`)
      .then((res) => setTeams(Array.isArray(res.data) ? res.data : []))
      .catch(() => showToast('Could not load teams', 'error'))
      .finally(() => setLoadingTeams(false));
  }, [seasonId, showToast]);

  const setOverride = (teamId, field, value) => {
    setOverrides((prev) => ({
      ...prev,
      [teamId]: { ...(prev[teamId] || {}), [field]: value },
    }));
  };

  const handleBuild = async () => {
    // Build the team_budgets payload: only include teams the admin actually edited.
    const team_budgets = {};
    for (const [teamId, cfg] of Object.entries(overrides)) {
      const budget = cfg.budget !== undefined && cfg.budget !== '' ? parseInt(cfg.budget, 10) : null;
      const owner_slots = cfg.owner_slots !== undefined && cfg.owner_slots !== '' ? parseInt(cfg.owner_slots, 10) : null;
      // skip rows where admin entered nothing meaningful
      if (budget === null && owner_slots === null) continue;
      team_budgets[teamId] = {
        budget: budget === null ? defaultBudget : budget,
        owner_slots: owner_slots === null ? 0 : owner_slots,
      };
    }

    setBuilding(true);
    setBuildResult(null);
    try {
      const res = await api.post('/auction/setup', {
        season_id: seasonId,
        players_per_set: parseInt(playersPerSet, 10),
        budget_per_team: parseInt(defaultBudget, 10),
        admin_password: adminPassword,
        team_budgets,
      }, { timeout: 120000 });
      setBuildResult(res.data);
      showToast(`Auction built — ${res.data.total_players} players, ${res.data.set_count} sets`);
      if (onBuilt) onBuilt();
    } catch (err) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Build failed';
      showToast(msg, 'error');
    } finally {
      setBuilding(false);
    }
  };

  if (hasSession && !buildResult) {
    return (
      <div className="setup-tab">
        <div className="setup-already">
          <p>An auction session already exists for this season.</p>
          <p className="setup-already-sub">
            Switch to the <strong>Live</strong> tab to run it. To rebuild, end the current
            session first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-tab">
      <div className="setup-config-card">
        <h3>Session config</h3>
        <div className="setup-config-row">
          <label>
            Players per set
            <input type="number" min={1} max={100} value={playersPerSet}
              onChange={(e) => setPlayersPerSet(e.target.value)} />
          </label>
          <label>
            Default budget
            <input type="number" min={1} value={defaultBudget}
              onChange={(e) => setDefaultBudget(e.target.value)} />
          </label>
        </div>
        <p className="setup-hint">
          Set count is computed from verified players ÷ players-per-set. Setup re-fetches
          each player's rank from Henrik (≈2–3 min for a full season).
        </p>
      </div>

      <div className="setup-teams-card">
        <div className="setup-teams-head">
          <h3>Per-team budgets <span>(owner deduction)</span></h3>
          <span className="setup-teams-hint">Leave blank = {defaultBudget} budget, 0 owner slots</span>
        </div>

        {loadingTeams ? (
          <p className="setup-loading">Loading teams…</p>
        ) : teams.length === 0 ? (
          <p className="setup-loading">No teams found for this season.</p>
        ) : (
          <table className="setup-teams-table">
            <thead>
              <tr>
                <th>Team</th>
                <th>Start budget</th>
                <th>Owner slots</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.id}>
                  <td className="stt-name">{t.name}</td>
                  <td>
                    <input type="number" min={0} placeholder={String(defaultBudget)}
                      value={overrides[t.id]?.budget ?? ''}
                      onChange={(e) => setOverride(t.id, 'budget', e.target.value)} />
                  </td>
                  <td>
                    <input type="number" min={0} max={6} placeholder="0"
                      value={overrides[t.id]?.owner_slots ?? ''}
                      onChange={(e) => setOverride(t.id, 'owner_slots', e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="setup-refresh-card">
        <div className="setup-refresh-head">
          <h3>Player prices <span>(5-act, locked at verify)</span></h3>
          <button className="setup-refresh-btn" onClick={handleRefreshPrices}
            disabled={refreshing}>
            {refreshing ? 'Refreshing…' : '↻ Refresh all prices'}
          </button>
        </div>
        <p className="setup-hint">
          Re-pulls each verified player's rank from Henrik and recomputes the 5-act base
          price. Run before building to catch last-minute rank climbers.
        </p>
        {refreshProgress && (
          <div className="setup-refresh-progress">
            {refreshProgress.done}/{refreshProgress.total} priced
            {' · '}{refreshProgress.henrik_ok} via Henrik
            {' · '}{refreshProgress.fallback} fallback
          </div>
        )}
      </div>

      <button className="setup-build-btn" onClick={handleBuild}
        disabled={building || loadingTeams || teams.length === 0}>
        {building ? 'Building… (this takes a few minutes)' : 'Build auction · create sets + budgets'}
      </button>

      {buildResult && (
        <div className="setup-result">
          <h3>✓ Auction built</h3>
          <div className="setup-result-stats">
            <span>{buildResult.total_players} players</span>
            <span>{buildResult.set_count} sets</span>
            <span>{buildResult.teams_initialized} teams</span>
            {buildResult.fallback_priced_count > 0 && (
              <span className="setup-result-warn">
                {buildResult.fallback_priced_count} used fallback price
              </span>
            )}
          </div>
          <p className="setup-result-next">Verify in Supabase, then switch to the Live tab.</p>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LIVE TAB — run the auction
// ════════════════════════════════════════════════════════════════
function LiveTab({ seasonId, adminPassword, state, loading, refetch, showToast }) {
  const [actionLoading, setActionLoading] = useState(false);
  const [bidInput, setBidInput] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [confirmEnd, setConfirmEnd] = useState(false);

  // Animation state for sold/unsold
  const [cardAnim, setCardAnim] = useState(null);
  const prevPlayerRef = useRef(null);
  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (!state) return;
    const prev = prevPlayerRef.current;
    const prevStatus = prevStatusRef.current;
    const cur = state.current_player;
    if (prev && !cur && prevStatus === 'on_block') {
      const lastResult = state.recent_results?.[0];
      if (lastResult?.action === 'unsold') setCardAnim('unsold');
      else if (lastResult) setCardAnim('sold');
      setTimeout(() => setCardAnim(null), 2000);
    }
    prevPlayerRef.current = cur;
    prevStatusRef.current = cur ? 'on_block' : null;
  }, [state]);

  const session = state?.session_id ? state : null;
  const currentPlayer = state?.current_player || null;
  const teamBudgets = state?.team_budgets || [];
  const queuedPlayers = state?.queued_players || [];
  const recentResults = state?.recent_results || [];
  const isCompleted = session?.status === 'completed';
  const isPool = session?.status === 'replacement_pool';

  const doAction = useCallback(async (fn, successMsg) => {
    setActionLoading(true);
    try {
      const res = await fn();
      // next-player can signal a finished set
      if (res?.data?.set_complete) {
        showToast(`Set ${res.data.set_number} complete — pick the next set`, 'success');
      } else if (successMsg) {
        showToast(successMsg);
      }
      setTimeout(refetch, 500);
      return res;
    } catch (err) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Action failed';
      showToast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  }, [refetch, showToast]);

  // ── The two main buttons ──────────────────────────────────────
  const handleRandomSet = () =>
    doAction(async () => {
      const res = await api.post('/auction/random-set', { session_id: session?.session_id, admin_password: adminPassword });
      if (res?.data?.done) showToast('All sets done — start the unsold round', 'success');
      else if (res?.data?.active_set) showToast(`Set ${res.data.active_set} is live`);
      return res;
    });

  const handleRandomPlayer = () =>
    doAction(() => api.post('/auction/next-player', { session_id: session?.session_id, admin_password: adminPassword }));

  // Manual override: block a specific queued player
  const handleSetPlayer = (auctionPlayerId) =>
    doAction(() => api.post('/auction/next-player', {
      session_id: session?.session_id, player_id: auctionPlayerId, admin_password: adminPassword,
    }), 'Player set on block');

  const handleBid = (amount) => {
    const num = parseInt(amount, 10);
    if (!num || num < 1) { showToast('Enter a valid bid', 'error'); return; }
    doAction(() => api.post('/auction/bid', { session_id: session?.session_id, amount: num, admin_password: adminPassword }),
      `Bid updated to ${num} pts`);
  };

  const handleQuickBid = (delta) => {
    const base = session?.current_bid || 0;
    doAction(() => api.post('/auction/bid', { session_id: session?.session_id, amount: base + delta, admin_password: adminPassword }),
      `Bid → ${base + delta} pts`);
  };

  const handleSold = () => {
    if (!selectedTeamId) { showToast('Select a team first', 'error'); return; }
    doAction(() => api.post('/auction/sold', {
      session_id: session?.session_id,
      player_id: currentPlayer?.id,
      team_id: selectedTeamId,
      amount: session?.current_bid,
      is_unsold_round: session?.status === 'unsold_round' || session?.status === 'replacement_pool',
      admin_password: adminPassword,
    }), 'SOLD!');
    setSelectedTeamId('');
    setBidInput('');
  };

  const handleUnsold = () =>
    doAction(() => api.post('/auction/unsold', {
      session_id: session?.session_id,
      player_id: currentPlayer?.id,
      is_unsold_round: session?.status === 'unsold_round' || session?.status === 'replacement_pool',
      admin_password: adminPassword,
    }), 'Marked UNSOLD');

  const handleStartUnsoldRound = () =>
    doAction(() => api.post('/auction/start-unsold-round', { session_id: session?.session_id, admin_password: adminPassword }),
      'Unsold round started');

  const handleStartPool = () =>
    doAction(() => api.post('/auction/start-replacement-pool', { session_id: session?.session_id, admin_password: adminPassword }),
      'Replacement pool started');

  const handleEndAuction = () => {
    if (!confirmEnd) { setConfirmEnd(true); return; }
    doAction(() => apiService.endAuction(session?.session_id, adminPassword), 'Auction ended ✓');
    setConfirmEnd(false);
  };

  const handleRevert = (auctionPlayerId, playerName) => {
    if (!window.confirm(`Revert sale of ${playerName}? Restores them to the block and refunds the budget.`)) return;
    doAction(() => apiService.revertSold(session?.session_id, auctionPlayerId, adminPassword),
      `Sale of ${playerName} reverted`);
  };

  if (loading && !session) {
    return <div className="admin-no-session"><p>Loading…</p></div>;
  }

  if (!session) {
    return (
      <div className="admin-no-session">
        <p>No active auction session. Build one in the Setup tab first.</p>
      </div>
    );
  }

  return (
    <div className="admin-root-inner">
      <header className="admin-topbar">
        <span className="admin-title">VPL S5 — Live Auction</span>
        <span className="admin-session-info">
          {session?.status?.toUpperCase()} | SET {session?.current_set}
        </span>
        <div className="admin-topbar-actions">
          <button className="admin-refresh-btn" onClick={refetch} title="Force refresh">↺</button>
          {!isCompleted && (
            <button
              className={`admin-btn ${confirmEnd ? 'admin-btn-end-confirm' : 'admin-btn-end'}`}
              onClick={handleEndAuction} disabled={actionLoading}
              title={confirmEnd ? 'Click again to confirm' : 'End auction session'}>
              {confirmEnd ? '⚠ Confirm End' : 'End Auction'}
            </button>
          )}
          {isCompleted && <span className="admin-completed-badge">✓ COMPLETED</span>}
        </div>
      </header>

      <div className="admin-body">
        {/* Active-set queue */}
        <aside className="admin-queue">
          <h3>Active set · {queuedPlayers.length} pending</h3>
          <div className="queue-list">
            {queuedPlayers.length === 0 && (
              <p className="queue-empty">No players queued. Click “Random next set”.</p>
            )}
            {queuedPlayers.map((p) => (
              <div key={p.auction_player_id} className="queue-item">
                <div className="qi-info">
                  <span className="qi-name">{p.game_name}</span>
                  <span className="qi-role">{p.prim_role}</span>
                  <span className="qi-rank">{p.peak_rank || p.cur_rank}</span>
                </div>
                <button className="qi-set-btn" onClick={() => handleSetPlayer(p.auction_player_id)}
                  disabled={actionLoading} title="Manually put this player on block">Set</button>
              </div>
            ))}
          </div>
        </aside>

        {/* Bid controls */}
        <main className="admin-controls">
          <div className={`admin-player-card ${cardAnim === 'sold' ? 'anim-sold' : cardAnim === 'unsold' ? 'anim-unsold' : ''}`}>
            {currentPlayer ? (
              <>
                <div className="apc-name">{currentPlayer.game_name}</div>
                <div className="apc-sub">{currentPlayer.role} · {currentPlayer.peak_rank || currentPlayer.cur_rank} · base {currentPlayer.base_price}</div>
                <div className="apc-bid">{session?.current_bid || 0} pts</div>
              </>
            ) : (
              <div className="apc-empty">No player on block — click “Random next player”</div>
            )}
          </div>

          <div className="bid-quick-row">
            {[1, 2, 4, 8, 12, 16, 20].map((delta) => (
              <button key={delta} className="bid-quick-btn"
                onClick={() => handleQuickBid(delta)}
                disabled={actionLoading || !currentPlayer || isCompleted}>
                +{delta}
              </button>
            ))}
          </div>

          <div className="bid-manual-row">
            <input type="number" min={1} placeholder="Manual bid" value={bidInput}
              onChange={(e) => setBidInput(e.target.value)}
              className="bid-manual-input" disabled={!currentPlayer || isCompleted} />
            <button className="admin-btn admin-btn-secondary"
              onClick={() => handleBid(bidInput)}
              disabled={actionLoading || !currentPlayer || !bidInput || isCompleted}>
              Set Bid
            </button>
          </div>

          <div className="sold-row">
            <select className="team-select" value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              disabled={!currentPlayer || isCompleted}>
              <option value="">— Select buying team —</option>
              {teamBudgets.map((tb) => {
                const full = tb.players_bought >= 6;
                return (
                  <option key={tb.team_id} value={tb.team_id} disabled={full}>
                    {tb.team_name} ({tb.available_budget} pts · {tb.players_bought}/6){full ? ' — FULL' : ''}
                  </option>
                );
              })}
            </select>
            <button className="admin-btn admin-btn-sold" onClick={handleSold}
              disabled={actionLoading || !currentPlayer || !selectedTeamId || isCompleted}>
              {actionLoading ? '…' : '✓ SOLD'}
            </button>
            <button className="admin-btn admin-btn-unsold" onClick={handleUnsold}
              disabled={actionLoading || !currentPlayer || isCompleted}>
              ✗ UNSOLD
            </button>
          </div>

          {/* ── The two main flow buttons + phase triggers ── */}
          <div className="live-flow-row">
            <button className="admin-btn admin-btn-primary live-flow-main"
              onClick={handleRandomPlayer}
              disabled={actionLoading || isCompleted || !!currentPlayer}
              title={currentPlayer ? 'Resolve current player first' : 'Block a random player from the active set'}>
              🎲 Random next player
            </button>
            <button className="admin-btn admin-btn-secondary live-flow-main"
              onClick={handleRandomSet}
              disabled={actionLoading || isCompleted}
              title="Pick a random unplayed set">
              🎲 Random next set
            </button>
          </div>

          <div className="live-phase-row">
            <button className="admin-btn admin-btn-secondary"
              onClick={handleStartUnsoldRound}
              disabled={actionLoading || isCompleted}
              title="Bring unsold players back at base +2">
              Start unsold round
            </button>
            <button className="admin-btn admin-btn-secondary"
              onClick={handleStartPool}
              disabled={actionLoading || isCompleted}
              title="Move still-unsold players to the replacement pool">
              Start replacement pool
            </button>
          </div>

          {isPool && (
            <div className="live-pool-banner">
              Replacement pool active — only teams with open roster slots can buy.
            </div>
          )}

          {recentResults.length > 0 && (
            <div className="admin-recent">
              <h4 className="admin-recent-title">Recent Sales</h4>
              {recentResults.slice(0, 5).map((r, i) => (
                <div key={i} className={`admin-recent-row ${r.action === 'unsold' ? 'rr-unsold' : ''}`}>
                  <span className="rr-name">{r.player_name}</span>
                  <span className="rr-team">{r.team_name || '—'}</span>
                  <span className="rr-amount">
                    {r.action === 'unsold' ? 'UNSOLD' : `${r.amount} pts`}
                  </span>
                  {i === 0 && r.action !== 'unsold' && r.auction_player_id && (
                    <button className="rr-revert-btn"
                      onClick={() => handleRevert(r.auction_player_id, r.player_name)}
                      disabled={actionLoading || isCompleted} title="Undo this sale">
                      ↩ Revert
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Team budgets */}
        <aside className="admin-budgets">
          <h3>Team Budgets</h3>
          <div className="budget-list">
            {teamBudgets.map((tb) => (
              <div key={tb.team_id} className="budget-row">
                <span className="br-name">{tb.team_name}</span>
                <span className="br-remaining">{tb.available_budget} pts</span>
                <span className="br-players">{tb.players_bought}/6</span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SHELL — tabs + poller
// ════════════════════════════════════════════════════════════════
function AuctionAdminPanel({ adminPassword }) {
  const currentSeason = useStore((s) => s.currentSeason);
  const seasonId = currentSeason?.id;
  const { state, loading, refetch } = useAuctionPoller(seasonId, true);

  const [tab, setTab] = useState('live');
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') =>
    setToast({ message, type, id: Date.now() }), []);

  const hasSession = !!(state?.session_id);

  // Default to setup tab if no session exists yet
  useEffect(() => {
    if (!loading && !hasSession) setTab('setup');
  }, [loading, hasSession]);

  return (
    <div className="admin-root">
      {toast && <Toast message={toast.message} type={toast.type} key={toast.id} onClose={() => setToast(null)} />}

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'setup' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('setup')}>Setup</button>
        <button className={`admin-tab ${tab === 'live' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('live')}>Live</button>
      </div>

      {tab === 'setup' ? (
        <SetupTab
          seasonId={seasonId}
          adminPassword={adminPassword}
          hasSession={hasSession}
          onBuilt={refetch}
          showToast={showToast}
        />
      ) : (
        <LiveTab
          seasonId={seasonId}
          adminPassword={adminPassword}
          state={state}
          loading={loading}
          refetch={refetch}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function AuctionAdmin() {
  const [adminPassword, setAdminPassword] = useState(null);
  if (!adminPassword) return <PasswordGate onUnlock={setAdminPassword} />;
  return <AuctionAdminPanel adminPassword={adminPassword} />;
}

export default AuctionAdmin;